import { z } from 'zod';

/**
 * Tipos de refund — alinhados ao enum `RefundType` do Prisma.
 *
 * MVP (story 4.11) só suporta `manual` — operador registra reembolso feito
 * fora do Asaas (PIX direto pro cliente, TED, depósito). `asaas_full` /
 * `asaas_partial` ficam pra story futura quando integrarmos refund via Asaas SDK.
 */
export const refundTypeSchema = z.enum(['manual', 'asaas_full', 'asaas_partial']);
export type RefundType = z.infer<typeof refundTypeSchema>;

/**
 * Body do `POST /api/refunds` — registra refund manual.
 *
 * Validações no schema:
 * - amount: positivo, máx R$ 999.999,99, precision 2 casas
 * - type: literal `manual` no MVP
 * - reason: 1-500 chars (motivo do reembolso — obrigatório AC7)
 * - externalReference: obrigatório (PIX id, comprovante TED, etc)
 * - attachmentUrl: opcional (URL externa do comprovante)
 * - refundedAt: ISO 8601 — data real do reembolso
 *
 * Validações no service (não dá pra fazer só no schema):
 * - charge existe + status in ('paga', 'paga_manual')
 * - amount + sum(refunds existentes) <= charge.amount
 */
export const createRefundSchema = z.object({
  chargeId: z.string().uuid('chargeId deve ser UUID válido'),
  amount: z
    .number()
    .positive('Valor deve ser maior que zero')
    .max(999_999.99, 'Valor excede limite máximo')
    .multipleOf(0.01, 'Valor deve ter no máximo 2 casas decimais'),
  type: z.literal('manual'),
  reason: z
    .string()
    .trim()
    .min(1, 'Motivo obrigatório')
    .max(500, 'Motivo deve ter no máximo 500 caracteres'),
  externalReference: z
    .string()
    .trim()
    .min(1, 'Referência obrigatória (ID PIX, comprovante TED)')
    .max(200, 'Referência deve ter no máximo 200 caracteres'),
  attachmentUrl: z.string().url('URL inválida').max(1000, 'URL muito longa').optional(),
  refundedAt: z
    .string()
    .datetime({ message: 'Data deve estar no formato ISO 8601 (ex: 2026-06-02T14:30:00Z)' }),
});
export type CreateRefundRequest = z.infer<typeof createRefundSchema>;

/** Query params do `GET /api/refunds`. */
export const listRefundsQuerySchema = z.object({
  chargeId: z.string().uuid('chargeId deve ser UUID válido'),
});
export type ListRefundsQuery = z.infer<typeof listRefundsQuerySchema>;

/** Item de refund (shape do response). */
export const refundItemSchema = z.object({
  id: z.string().uuid(),
  chargeId: z.string().uuid(),
  clientId: z.string().uuid(),
  amount: z.string(),
  type: refundTypeSchema,
  reason: z.string().nullable(),
  externalReference: z.string().nullable(),
  attachmentUrl: z.string().nullable(),
  byUserId: z.string().uuid(),
  byUserName: z.string().nullable(),
  refundedAt: z.string(),
  createdAt: z.string(),
});
export type RefundItem = z.infer<typeof refundItemSchema>;

export const createRefundResponseSchema = z.object({
  data: refundItemSchema,
});
export type CreateRefundResponse = z.infer<typeof createRefundResponseSchema>;

export const listRefundsResponseSchema = z.object({
  data: z.array(refundItemSchema),
  meta: z.object({
    chargeAmount: z.string(),
    refundedTotal: z.string(),
    remainingBalance: z.string(),
  }),
});
export type ListRefundsResponse = z.infer<typeof listRefundsResponseSchema>;
